import { useMemo } from "react";
import { Entity } from "@common/types/entity";
import { useLedgers } from "@api/ledger.api";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { LedgerBasisSelect, LedgerBasisOption } from "./LedgerBasisSelect";

interface Props {
    selectedEntity?: Entity;
    selectedLedgerId?: string;
    onChange: (ledgerId: string) => void;
    disabled?: boolean;
    className?: string;
    buttonSize?: TriggerButtonProps["size"];
    /** Appended after the entity's ledgers. Lets callers offer selections that
     *  are not a single ledger, such as the journal entry "All" option. */
    extraOptions?: LedgerBasisOption[];
    /** Per-ledger disabled/tooltip override, keyed by ledger id. Lets callers
     *  keep every ledger visible while disabling the ones that aren't a valid
     *  selection right now (e.g. a ledger with no journal entry for the
     *  transaction being viewed), with a tooltip explaining why. */
    ledgerOptionOverrides?: ReadonlyMap<
        string,
        { disabled?: boolean; tooltip?: string }
    >;
}

export const LedgerSelect: React.FC<Props> = ({
    selectedEntity,
    selectedLedgerId,
    onChange,
    disabled,
    className,
    buttonSize = "md",
    extraOptions,
    ledgerOptionOverrides,
}) => {
    const { data: ledgers } = useLedgers(selectedEntity?.id);

    const options = useMemo(
        () => [
            ...(ledgers ?? []).map((ledger) => {
                const override = ledgerOptionOverrides?.get(ledger.id);
                return {
                    value: ledger.id,
                    label: ledger.name || getAccountingBasisLabel(ledger.basis),
                    disabled: override?.disabled,
                    tooltip: override?.tooltip,
                };
            }),
            ...(extraOptions ?? []),
        ],
        [ledgers, extraOptions, ledgerOptionOverrides],
    );

    if (!selectedEntity) {
        return null;
    }

    return (
        <LedgerBasisSelect
            options={options}
            value={selectedLedgerId}
            onChange={onChange}
            disabled={disabled}
            className={className}
            buttonSize={buttonSize}
        />
    );
};
